<script setup lang="ts">
// Structured add/remove rows for a task's input fields, with a JSON toggle for power users.
// Every field is the kit's (#1227): the text fields are the kit `Input`, the type the kit `Select`, the Required
// tick the kit `Checkbox`, and each stored row sits on a kit surface. It states no border, fill or radius of its own.
//
// A field is a name and a plain type (#1754). The key a skill reads it by is derived from the name when the field is
// added and never shown or changed after: renaming a field changes its label, so the skill reading it never breaks.
// The JSON toggle still shows the whole stored shape, key included.

import {
    Button,
    Checkbox,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhPanel,
} from '@zerohuman/ui'
import { ref, useId } from 'vue'
import type { TaskInputField } from '@/api'
import RowsJsonSection from '@/components/RowsJsonSection.vue'
import { useRowsModel } from '@/composables/useRowsModel'
import { INPUT_TYPE_CHOICES, inputKeyOf } from '@/lib/inputFields'

/** The add row: what a person says about a new field. Its key comes from the name when it is added. */
type FieldDraft = { name: string; type: TaskInputField['type']; required: boolean; configuration: boolean }

const props = defineProps<{ modelValue: TaskInputField[] }>()
const emit = defineEmits<{ 'update:modelValue': [TaskInputField[]] }>()

const { rows, showJson, jsonText, jsonError, append, removeAt, toggleJson } = useRowsModel<
    TaskInputField,
    TaskInputField
>({
    modelValue: () => props.modelValue,
    emit: value => emit('update:modelValue', value),
    // Rows hold the field as stored; unknown extras (placeholder, default) survive an edit untouched.
    toRow: field => ({ ...field }),
    // An optional field is stored without `required` at all, whether it was added that way or the tick
    // was taken off later: the checkbox writes an explicit `false`, which is dropped here so the two
    // paths cannot drift into two shapes for the same thing.
    toValue: row => {
        const { required, ...rest } = row
        return required ? { ...rest, required: true } : rest
    },
})

const draft = ref<FieldDraft>(emptyDraft())

/** Why the last add was refused, shown beside the add row until a field is added. */
const addError = ref<string | null>(null)

/** The kit Select's trigger is a button, so it is named by the words beside it rather than wrapped in a label. */
const fieldId = useId()

function emptyDraft(): FieldDraft {
    return { name: '', type: 'string', required: false, configuration: false }
}

/**
 * The key is derived from the name here, once: it is the field's identity, so it is never renamed on a row. A name
 * with no letter or digit has no key to give, and one whose key the task already has would make two fields one, so
 * both are refused with a message and nothing is added. A field that was not ticked is stored without `required`.
 */
function add(): void {
    const name = draft.value.name.trim()
    const key = inputKeyOf(name)
    if (!key) {
        addError.value = 'Give the input a name with at least one letter or digit.'
        return
    }
    if (rows.value.some(row => row.key === key)) {
        addError.value = 'This task already has an input with that name.'
        return
    }
    const field: TaskInputField = { key, label: name, type: draft.value.type }
    if (draft.value.required) {
        field.required = true
    }
    if (draft.value.configuration) field.configuration = true
    append(field)
    addError.value = null
    draft.value = emptyDraft()
}
</script>

<template>
	<RowsJsonSection
		testid="task-inputs"
		title="Inputs"
		:show-json="showJson"
		:json-text="jsonText"
		:json-error="jsonError"
		:json-rows="6"
		@update:json-text="jsonText = $event"
		@toggle="toggleJson"
	>
		<template #description>What a run of this task is given: a name for each field and the kind of value it takes.</template>
		<p v-if="!rows.length" class="text-muted-foreground text-sm">No inputs yet.</p>
		<ul class="space-y-2">
			<ZhPanel
				v-for="(row, index) in rows"
				:key="index"
				as="li"
				data-testid="task-input-row"
				class="flex flex-wrap items-end gap-2"
			>
				<label class="grid gap-1 text-xs">
					Name
					<Input v-model="row.label" />
				</label>
				<div class="grid gap-1 text-xs">
					<span :id="`${fieldId}-type-${index}`">Type</span>
					<Select v-model="row.type">
						<SelectTrigger :aria-labelledby="`${fieldId}-type-${index}`">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="choice in INPUT_TYPE_CHOICES" :key="choice.type" :value="choice.type">{{ choice.label }}</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<label class="flex items-center gap-1 text-xs">
					<Checkbox v-model="row.required" />
					Required
				</label>
                <label class="flex items-center gap-1 text-xs">
                    <Checkbox v-model="row.configuration" />
                    Persistent configuration
                </label>
				<Button type="button" size="sm" variant="destructive" data-testid="task-input-remove" @click="removeAt(index)">
					Remove
				</Button>
			</ZhPanel>
		</ul>
		<div class="flex flex-wrap items-end gap-2">
			<label class="grid gap-1 text-xs">
				Name
				<Input v-model="draft.name" data-testid="task-input-add-name" placeholder="Issue number" />
			</label>
			<div class="grid gap-1 text-xs">
				<span :id="`${fieldId}-add-type`">Type</span>
				<Select v-model="draft.type" data-testid="task-input-add-type">
					<SelectTrigger :aria-labelledby="`${fieldId}-add-type`">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="choice in INPUT_TYPE_CHOICES" :key="choice.type" :value="choice.type">{{ choice.label }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<label class="flex items-center gap-1 text-xs">
				<Checkbox v-model="draft.required" data-testid="task-input-add-required" />
				Required
			</label>
            <label class="flex items-center gap-1 text-xs">
                <Checkbox v-model="draft.configuration" />
                Persistent configuration
            </label>
			<Button type="button" size="sm" variant="outline" data-testid="task-input-add" @click="add">Add input</Button>
		</div>
		<p v-if="addError" data-testid="task-input-add-error" role="alert" class="text-destructive text-xs">{{ addError }}</p>
	</RowsJsonSection>
</template>
